import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BottomNavIcon } from "./BottomNavIcon";
import { Popup } from "./components/Popup";
import { PopupContent } from "./components/PopupContent";
import { Sidebar, SidebarSection, SidebarItem } from "./components/Sidebar";
import { useNavigate } from "react-router";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { hasPermission as checkPermission } from "@/lib/utils";
import { PERMISSIONS } from "@/constants/permissions";
import { getBFFProductTaxonomyNavItems } from "@/constants/bffProductSegment";

export function BottomNavbar() {
  const navigate = useNavigate();
  const { permissions } = useUserPermissions();
  
  const hasPermission = useCallback(
    (perm) => checkPermission(permissions, perm),
    [permissions]
  );

  // Compute permitted popup/sidebar contents dynamically (matching CRM desktop sidebar)
  const permittedPopupContents = useMemo(() => {
    const contents = {};

    // 1. Clients — direct navigation (no popup), handled separately in handleTabClick
    if (hasPermission(PERMISSIONS.CLIENT.READ)) {
      contents.clients = {
        type: "navigate",
        path: "/clients",
      };
    }

    // 2. Campaigns popup
    if (hasPermission(PERMISSIONS.CAMPAIGN.READ)) {
      contents.campaigns = {
        type: "popup",
        title: "Campaigns",
        items: [
          { label: "Applied Sample Promotion", to: "/campaigns/applied-sample-promotion" },
          { label: "BFF Product Promotion", to: "/campaigns/bff-product-promotion" },
          { label: "Promotion & Adoption Report", to: "/campaigns/promotion-adoption-report" },
        ],
      };
    }

    // 3. CRM Settings popup
    const settingsItems = [
      ...(hasPermission(PERMISSIONS.CRM_SALES_TEAM.READ) ? [{ label: "Sales Teams", to: "/settings/sales-teams" }] : []),
      ...(hasPermission(PERMISSIONS.CRM_DEFAULT_FOLLOWER.READ) ? [{ label: "Default Followers", to: "/settings/default-followers" }] : []),
      ...(hasPermission(PERMISSIONS.CRM_DEFAULT_MANAGER.READ) ? [{ label: "Default CRM Managers", to: "/settings/default-managers" }] : []),
      ...(hasPermission(PERMISSIONS.CRM_MARKET_CATEGORY.READ) ? [{ label: "CRM Market", to: "/settings/market-categories" }] : []),
      ...(hasPermission(PERMISSIONS.CRM_CATEGORY.READ) ? [{ label: "Categories", to: "/settings/categories" }] : []),
      ...(hasPermission(PERMISSIONS.CRM_TAG.READ) ? [{ label: "CRM Tags", to: "/settings/tags" }] : []),
    ];
    contents.settings = {
      type: "popup",
      title: "CRM Settings",
      items: settingsItems,
    };

    // 4. Invites and Access popup
    const accessItems = [
      ...(hasPermission(PERMISSIONS.INVITATION.READ) ? [{ label: "Employee Invitations", to: "/employee-invitations" }] : []),
      ...(hasPermission(PERMISSIONS.USER.READ) ? [{ label: "Employee Management", to: "/employee-management" }] : []),
      ...(hasPermission(PERMISSIONS.ROLE.READ) ? [{ label: "Access Management", to: "/access-management" }] : []),
      ...(hasPermission(PERMISSIONS.AUDIT.READ) ? [{ label: "History", to: "/access-history" }] : []),
    ];
    if (accessItems.length > 0) {
      contents.access = {
        type: "popup",
        title: "Invites and Access",
        items: accessItems,
      };
    }

    // 5. More sidebar (Profile + BFF Product)
    const moreSections = [
      {
        title: "General",
        items: [
          { label: "Profile", to: "/profile" },
        ],
      },
      ...(() => {
        const bffPerm = PERMISSIONS.BFF_PRODUCT || PERMISSIONS.BFF_PRODUCT_CODE;
        const bffTaxPerm = PERMISSIONS.BFF_PRODUCT_SEGMENT || PERMISSIONS.BFF_PRODUCT_TAXONOMY;
        const items = [
          ...(hasPermission(bffPerm.READ)
            ? [{ label: "Product List", to: "/bff-product/list" }]
            : []),
          ...(hasPermission(bffTaxPerm.READ) || hasPermission("bff-product-taxonomy:read")
            ? getBFFProductTaxonomyNavItems().map(({ label, path }) => ({ label, to: path }))
            : []),
        ];
        return items.length > 0 ? [{ title: "BFF Product List", items }] : [];
      })(),
    ];

    contents.more = {
      type: "sidebar",
      sections: moreSections,
    };

    return contents;
  }, [hasPermission]);

  // Compute dynamic NAV_ITEMS based on permitted popup/sidebar contents (matching CRM desktop sidebar)
  const NAV_ITEMS = useMemo(() => {
    const items = [];
    if (permittedPopupContents.clients) {
      items.push({ id: "clients", label: "Clients", iconName: "team-management" });
    }
    if (permittedPopupContents.campaigns) {
      items.push({ id: "campaigns", label: "Campaigns", iconName: "team-management" });
    }
    if (permittedPopupContents.settings) {
      items.push({ id: "settings", label: "Settings", iconName: "team-management" });
    }
    if (permittedPopupContents.access) {
      items.push({ id: "access", label: "Access", iconName: "invite-access" });
    }
    // "More" (expanded options) is always present
    items.push({ id: "more", label: "More", iconName: "more" });
    return items;
  }, [permittedPopupContents]);

  const [activeTab, setActiveTab] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 375
  );

  // Sync activeTab with dynamic NAV_ITEMS
  useEffect(() => {
    if (NAV_ITEMS.length > 0 && (!activeTab || !NAV_ITEMS.some((item) => item.id === activeTab))) {
      setActiveTab(NAV_ITEMS[0].id);
    }
  }, [NAV_ITEMS, activeTab]);

  // Track window width for dynamic SVG sizing
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Calculate the position of the "dip" based on the active tab index
  const activeIndex = NAV_ITEMS.findIndex((item) => item.id === activeTab);

  // Dynamic item width based on current window width
  const itemWidth = windowWidth / NAV_ITEMS.length;
  const centerPos = activeIndex !== -1 ? activeIndex * itemWidth + itemWidth / 2 : 0;

  const handleTabClick = (id) => {
    const config = permittedPopupContents[id];

    if (config?.type === "navigate") {
      // For direct navigation items (e.g. Clients), navigate immediately
      setActiveTab(id);
      setIsPopupOpen(false);
      setIsSidebarOpen(false);
      navigate(config.path);
    } else if (config?.type === "sidebar") {
      // For "More" button, toggle sidebar
      if (activeTab === id) {
        setIsSidebarOpen(!isSidebarOpen);
      } else {
        setActiveTab(id);
        setIsSidebarOpen(true);
        setIsPopupOpen(false);
      }
    } else {
      // For other buttons, toggle popup
      if (activeTab === id) {
        setIsPopupOpen(!isPopupOpen);
        setIsSidebarOpen(false);
      } else {
        setActiveTab(id);
        setIsPopupOpen(true);
        setIsSidebarOpen(false);
      }
    }
  };

  // --- SVG PATHS ---
  // Symmetric Design with "Circle Edge" look.
  const curveWidth = 118;
  const curveDepth = 32;
  const cpOffset = curveWidth / 4; // Adjusted for rounder curve

  const navbarPath = `
    M 0 0
    L ${centerPos - curveWidth / 2} 0
    C ${centerPos - curveWidth / 2 + cpOffset} 0, ${
    centerPos - cpOffset
  } ${curveDepth}, ${centerPos} ${curveDepth}
    C ${centerPos + cpOffset} ${curveDepth}, ${
    centerPos + curveWidth / 2 - cpOffset
  } 0, ${centerPos + curveWidth / 2} 0
    L ${windowWidth} 0
    L ${windowWidth} 80
    L 0 80
    Z
  `;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 flex flex-col justify-end pointer-events-none md:hidden">
      {/* Popup Layer */}
      <AnimatePresence>
        {isPopupOpen && permittedPopupContents[activeTab]?.type === "popup" && (
          <Popup
            isOpen={isPopupOpen}
            windowWidth={windowWidth}
            centerPos={centerPos}
            curveWidth={curveWidth}
            curveDepth={curveDepth}
            height={240}
            horizontalPadding={8}
          >
            <PopupContent
              title={permittedPopupContents[activeTab].title}
              items={permittedPopupContents[activeTab].items}
            />
          </Popup>
        )}
      </AnimatePresence>

      {/* Sidebar for "More" button */}
      <AnimatePresence>
        {isSidebarOpen && permittedPopupContents[activeTab]?.type === "sidebar" && (
          <Sidebar
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
            windowWidth={windowWidth}
            centerPos={centerPos}
            curveWidth={curveWidth}
            curveDepth={curveDepth}
          >
            {permittedPopupContents[activeTab].sections?.map((section, index) => (
              <SidebarSection key={index} title={section.title}>
                {section.items.map((item, itemIndex) => (
                  <SidebarItem
                    key={itemIndex}
                    label={item.label}
                    icon={item.icon}
                    onClick={() => {
                      if (item.to) {
                        const targetPath = item.to.startsWith("/") ? item.to : `/${item.to}`;
                        navigate(targetPath);
                      }
                      item.onClick?.();
                      setIsSidebarOpen(false);
                    }}
                  />
                ))}
              </SidebarSection>
            ))}
          </Sidebar>
        )}
      </AnimatePresence>

      {/* Navbar Layer */}
      <div className="relative w-full h-20 bg-transparent pointer-events-auto z-50">
        {/* Navbar Background SVG */}
        <div className="absolute inset-0 w-full h-full ">
          <svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${windowWidth} 80`}
            preserveAspectRatio="none"
            className="w-full h-full"
          >
            <motion.path
              d={navbarPath}
              className="fill-background"
              animate={{ d: navbarPath }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            />
          </svg>
        </div>

        {/* Floating Action Button */}
        <motion.div
          className="absolute -top-[30px] flex items-center justify-center w-14 h-14 bg-primary rounded-full text-white shadow-lg z-50"
          style={{ left: 0 }}
          animate={{ x: centerPos - 28 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {NAV_ITEMS.map((item) => {
            return activeTab === item.id ? (
              <BottomNavIcon
                key={item.id}
                name={item.iconName}
                className="w-6 h-6"
              />
            ) : null;
          })}
        </motion.div>

        {/* Navigation Items */}
        <div className="absolute inset-0 flex items-end justify-between px-0 pb-0 h-[96%]">
          {NAV_ITEMS.map((item) => {
            const isActive = activeTab === item.id;

            return (
              <button
                key={item.id}
                onClick={() => handleTabClick(item.id)}
                className={`flex flex-col items-center justify-end w-1/5 pb-4 transition-colors duration-300 z-50 ${
                  isActive ? "text-transparent" : "text-foreground"
                }`}
                style={{ height: "80px" }}
              >
                <div
                  className={`mb-1 transition-opacity duration-200 ${
                    isActive ? "opacity-0" : "opacity-100"
                  }`}
                >
                  <BottomNavIcon name={item.iconName} className="w-6 h-6" />
                </div>
                <span
                  className={`text-[12px] font-normal transition-all duration-200 ${
                    isActive
                      ? "text-nav-highlight translate-y-1 font-bold scale-130"
                      : "text-foreground scale-100"
                  }`}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
